import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Download, AlertCircle, CheckCircle2, Clock, Hourglass, XCircle, Trash2 } from "lucide-react";
import { SafetyAssignDialog } from "./SafetyAssignDialog";

type Row = {
  assignment_id: string; template_id: string; template_title: string; template_version: number;
  employee_id: string; employee_name: string; manager_employee_id: string | null;
  status: string; due_date: string | null; assigned_at: string;
  attempts_used: number; passed_at: string | null; certificate_id: string | null;
  days_until_due: number | null;
};

const STATUS_META: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; icon: any }> = {
  pending: { label: "Geplant", variant: "outline", icon: Clock },
  invited: { label: "Eingeladen", variant: "secondary", icon: Hourglass },
  in_progress: { label: "In Bearbeitung", variant: "secondary", icon: Hourglass },
  awaiting_review: { label: "Prüfung erforderlich", variant: "secondary", icon: AlertCircle },
  passed: { label: "Bestanden", variant: "default", icon: CheckCircle2 },
  failed: { label: "Nicht bestanden", variant: "destructive", icon: XCircle },
  expired: { label: "Abgelaufen", variant: "destructive", icon: XCircle },
  cancelled: { label: "Abgebrochen", variant: "outline", icon: XCircle },
};

export const SafetyAssignmentsManager = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_safety_assignments_overview");
    if (error) toast.error("Fehler beim Laden der Zuweisungen");
    setRows((data as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r =>
    r.template_title.toLowerCase().includes(search.toLowerCase()) ||
    r.employee_name.toLowerCase().includes(search.toLowerCase()) ||
    r.status.includes(search.toLowerCase()),
  );

  const downloadCert = async (path: string) => {
    const { data } = await supabase.storage.from("safety-certificates").createSignedUrl(path, 60);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const handleDownload = async (assignmentId: string) => {
    const { data: cert } = await supabase.from("safety_instruction_certificates")
      .select("storage_path").eq("assignment_id", assignmentId).maybeSingle();
    if (cert?.storage_path) downloadCert(cert.storage_path);
    else toast.error("Kein Zertifikat verfügbar");
  };

  const handleRevoke = async (assignment_id: string) => {
    if (!confirm("Zuweisung abbrechen? Die Magic-Link Einladung wird ungültig.")) return;
    await supabase.from("safety_instruction_assignments").update({ status: "cancelled", cancelled_at: new Date().toISOString() }).eq("id", assignment_id);
    await supabase.from("safety_instruction_invitations").update({ status: "revoked", revoked_at: new Date().toISOString() }).eq("assignment_id", assignment_id);
    toast.success("Zuweisung abgebrochen");
    load();
  };

  const handleDelete = async (assignment_id: string) => {
    if (!confirm("Diese Zuweisung inkl. aller Versuche und Einladungen unwiderruflich löschen?")) return;
    const { error } = await supabase.from("safety_instruction_assignments").delete().eq("id", assignment_id);
    if (error) { toast.error(error.message); return; }
    toast.success("Zuweisung gelöscht");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Suche..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Button onClick={() => setDlgOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> Zuweisen</Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mitarbeiter</TableHead>
              <TableHead>Unterweisung</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Fällig</TableHead>
              <TableHead>Versuche</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Lade...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Keine Zuweisungen</TableCell></TableRow>
            ) : filtered.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const overdue = r.days_until_due !== null && r.days_until_due < 0 && !["passed", "cancelled"].includes(r.status);
              return (
                <TableRow key={r.assignment_id}>
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-sm">{r.template_title} <span className="text-muted-foreground">v{r.template_version}</span></TableCell>
                  <TableCell>
                    <Badge variant={meta.variant} className="gap-1"><Icon className="h-3 w-3" />{meta.label}</Badge>
                  </TableCell>
                  <TableCell className={`text-sm ${overdue ? "text-destructive font-medium" : ""}`}>
                    {r.due_date ? new Date(r.due_date).toLocaleDateString("de-DE") : "—"}
                    {overdue && <div className="text-xs">{Math.abs(r.days_until_due!)} Tage überfällig</div>}
                  </TableCell>
                  <TableCell className="text-sm">{r.attempts_used}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {r.status === "passed" && (
                        <Button size="sm" variant="ghost" onClick={() => handleDownload(r.assignment_id)} className="gap-1">
                          <Download className="h-4 w-4" /> Zertifikat
                        </Button>
                      )}
                      {["invited", "in_progress", "pending"].includes(r.status) && (
                        <Button size="sm" variant="ghost" onClick={() => handleRevoke(r.assignment_id)}>Abbrechen</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.assignment_id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <SafetyAssignDialog open={dlgOpen} onOpenChange={setDlgOpen} onAssigned={load} />
    </div>
  );
};

export default SafetyAssignmentsManager;
